import React from 'react';
import { motion } from 'framer-motion';
import { Ticket, MessageSquareOff, ScrollText, CheckCircle } from 'lucide-react';
export function MechanismSection() {
  const mechanisms = [{
    icon: Ticket,
    title: '1) The Bedtime Pass',
    description: "Your child gets one after-goodnight request. Not unlimited. Not 'until they're satisfied.' One. It's fair (to them). And it makes requests finite (for you)."
  }, {
    icon: MessageSquareOff,
    title: '2) The Silent Return Protocol',
    description: 'After the pass is used—or if they pop out anyway—you stop feeding the loop with attention and explanations. You calmly walk them back. Minimal words. Same steps. Every time. No debate. No speeches. No accidental rewards.'
  }, {
    icon: ScrollText,
    title: '3) The 2-Minute Script Cards',
    description: "Because consistency is hardest when you're tired. So instead of improvising at 9pm, you'll have calm, firm, ready-to-use scripts for: water, potty loops, fear, 'I'm not tired,' 'lie with me,' yelling through the door, bargaining, 'one more question,' and more. Say less. Mean it more."
  }];
  return <section className="w-full bg-[#1e2a4a] pt-24 pb-0 text-[#faf6ef]" data-id="element-105">
      <div className="max-w-[680px] mx-auto px-6 pb-24" data-id="element-106">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-107">
          <h3 className="text-[#c9a96e] text-lg font-serif italic mb-4" data-id="element-108">
            Introducing: The Bedtime Pass + Boundary Script Kit (Ages 4–6)
          </h3>
          <p className="text-xl md:text-2xl font-serif text-[#faf6ef] mb-8 leading-relaxed" data-id="element-109">
            A premium, printable kit that removes the nightly
            negotiation—without turning your home into a punishment zone.
          </p>
          <p className="text-[#faf6ef]/80 text-lg" data-id="element-110">
            You'll use three simple tools that work together:
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-111">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#faf6ef]" data-id="element-112">
            The Unique Mechanism (Why This Works)
          </h2>
        </motion.div>

        <div className="space-y-8 mb-24" data-id="element-113">
          {mechanisms.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.2,
          duration: 0.6
        }} className="bg-[#faf6ef] text-[#1e2a4a] p-8 rounded-xl shadow-xl relative overflow-hidden group" data-id="element-114">
              <div className="absolute top-0 left-0 w-2 h-full bg-[#c9a96e]" data-id="element-115" />

              <div className="flex flex-col md:flex-row gap-6 items-start" data-id="element-116">
                <div className="bg-[#1e2a4a]/5 p-4 rounded-full flex-shrink-0" data-id="element-117">
                  <item.icon className="w-8 h-8 text-[#1e2a4a]" data-id="element-118" />
                </div>
                <div data-id="element-119">
                  <h3 className="text-2xl font-serif font-bold mb-4 text-[#1e2a4a]" data-id="element-120">
                    {item.title}
                  </h3>
                  <p className="text-lg leading-relaxed text-[#1e2a4a]/80" data-id="element-121">
                    {item.description}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* What Changes Subsection */}
      <div className="w-full bg-[#faf6ef] py-24 px-6 text-[#1e2a4a]" data-id="element-122">
        <div className="max-w-[680px] mx-auto" data-id="element-123">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-124">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-8 text-[#1e2a4a] text-center" data-id="element-125">
              What Changes (In Real Life)
            </h2>

            <div className="space-y-6 text-lg md:text-xl leading-[1.8] text-[#1e2a4a]/80 mb-12" data-id="element-126">
              <p data-id="element-127">
                When the rule is clear and the response is boring, your child
                stops "performing" for a new outcome.
              </p>
              <p data-id="element-128">
                You stop doing bedtime like a daily negotiation tournament.
                <br data-id="element-129" />
                You stop giving away your evening in tiny pieces.
              </p>
              <p className="font-medium text-[#1e2a4a]" data-id="element-130">
                And you get to feel like yourself again at 8:30pm:
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-131">
              {['calmer', 'more consistent', 'more in control', 'without being harsh'].map((item, index) => <div key={index} className="flex items-center bg-[#fffdf8] p-4 rounded-lg border border-[#c9a96e]/20 shadow-sm" data-id="element-132">
                  <CheckCircle className="w-5 h-5 text-[#c9a96e] mr-3" data-id="element-133" />
                  <span className="text-lg font-medium" data-id="element-134">{item}</span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}